import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, ExternalLink } from 'lucide-react';
import { DocumentService, Document } from '@/services/DocumentService';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

// Utility function to get a signed URL for a document
const getDocumentUrl = async (filePath: string): Promise<string> => {
  try {
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(filePath, 3600); // URL valid for 1 hour

    return data?.signedUrl || '';
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return '';
  }
};

export const AssignedDocuments = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      if (!user?.id) {
        setDocuments([]);
        return;
      }
      const docs = await DocumentService.getDocuments();
      setDocuments(docs);

      // Get signed URLs for all documents
      const urls: Record<string, string> = {};
      await Promise.all(
        docs.map(async (doc) => {
          if (doc.file_path) {
            urls[doc.id] = await getDocumentUrl(doc.file_path);
          }
        })
      );
      setDocumentUrls(urls);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = async (document: Document) => {
    try {
      const url = documentUrls[document.id];
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Unable to access document');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to open document');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="bg-card border border-border">
          <CardHeader className="pb-2">
            <div className="w-full h-4 bg-secondary/40 rounded animate-pulse mb-2"></div>
            <div className="w-2/3 h-3 bg-secondary/40 rounded animate-pulse"></div>
          </CardHeader>
          <CardContent>
            <div className="w-full h-20 bg-secondary/30 rounded animate-pulse"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="pt-6">
          <div className="text-center p-8">
            <h3 className="text-xl font-semibold mb-2">No Assigned Documents</h3>
            <p className="text-muted-foreground">
              You don't have any documents assigned to you at the moment.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      {documents.map((document) => (
        <Card key={document.id} className="bg-card border border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>{document.document_name}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {documentUrls[document.id] && (
                  <a
                    href={documentUrls[document.id]}
                    download={document.document_name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </a>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDocument(document)}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View
                </Button>
              </div>
            </div>
            <CardDescription>{document.document_type}</CardDescription>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span>{document.file_size} bytes</span>
              <span>•</span>
              <span>{new Date(document.uploaded_at || '').toLocaleDateString()}</span>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}; 